import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from "@air/primitive-button";
import { Modal, ModalTitle } from "@air/primitive-modal";
import { Spinner } from "@air/primitive-spinner";
import { useAirModal } from "@air/provider-modal";
import classNames from "classnames";
import { Formik } from "formik";
import { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import { CUSTOM_FIELD_DELETE, CUSTOM_FIELD_MODAL_CANCEL, CUSTOM_FIELD_MODAL_SAVE } from "~/constants/testIDs";
import { useCurrentWorkspacePermissionsContext } from "~/providers/CurrentWorkspacePermissionsProvider";
import { canDeleteCustomFields } from "~/utils/permissions/workspacePermissions";
import { CustomFieldDeleteModal } from "../CustomFieldDeleteModal";
import { CustomFieldModalForm } from "./CustomFieldModalForm/CustomFieldModalForm";
export const CUSTOM_FIELD_MODAL_FORM_ID = "customFieldModalForm";
export const CUSTOM_FIELD_MODAL_FORM_OPTIONS = "values";
export const CUSTOM_FIELD_MODAL_FORM_DESCRIPTION = "description";
export const CUSTOM_FIELD_MODAL_FORM_TITLE = "name";
export const CUSTOM_FIELD_MODAL_FORM_FIELD_TYPE = "fieldTypeId";
const customFieldSchema = Yup.object().shape({
    name: Yup.string().required("Title is required").max(40, "Title must be no more than 40 characters").default(""),
    description: Yup.string().default("").max(170, "Description must be no more than 170 characters"),
    values: Yup.array().of(Yup.object().shape({
        value: Yup.string().max(255, "Text must be no more than 35 characters"),
        color: Yup.object().shape({
            backgroundHex: Yup.string(),
            fontHex: Yup.string(),
            id: Yup.string(),
            name: Yup.string()
        })
    })).min(1, "Field must contain at least 1 option")
}).required();
export const CustomFieldModal = (param)=>{
    let { modalType, onClose, fieldTypes, initialFormValues, colors, handleSubmit, handleDelete, isLoading, isDataLoading } = param;
    const [isSaveDisabled, setIsSaveDisabled] = useState(false);
    const [isSaveFocused, setIsSaveFocused] = useState(false);
    const [showDeleteModal] = useAirModal(CustomFieldDeleteModal);
    const { data: permissions } = useCurrentWorkspacePermissionsContext();
    const canDelete = handleDelete && canDeleteCustomFields(permissions);
    const saveButtonRef = useRef(null);
    useEffect(()=>{
        var _saveButtonRef_current;
        if (isSaveFocused) (_saveButtonRef_current = saveButtonRef.current) === null || _saveButtonRef_current === void 0 ? void 0 : _saveButtonRef_current.focus();
    }, [
        isSaveFocused
    ]);
    return /*#__PURE__*/ _jsxs(Modal, {
        dangerouslyBypassFocusLock: true,
        isOpen: true,
        size: "medium",
        children: [
            /*#__PURE__*/ _jsxs(ModalTitle, {
                children: [
                    modalType === "edit" ? "Edit" : "Create",
                    " a custom field"
                ]
            }),
            !fieldTypes || isDataLoading ? /*#__PURE__*/ _jsx("div", {
                className: "mt-6 flex justify-center",
                children: /*#__PURE__*/ _jsx(Spinner, {
                    className: "text-teal-9"
                })
            }) : /*#__PURE__*/ _jsx(Formik, {
                initialValues: initialFormValues,
                validationSchema: customFieldSchema,
                onSubmit: handleSubmit,
                children: /*#__PURE__*/ _jsx(CustomFieldModalForm, {
                    modalType: modalType,
                    setIsSaveDisabled: setIsSaveDisabled,
                    setIsSaveFocused: setIsSaveFocused,
                    colors: colors,
                    fieldTypes: fieldTypes
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: classNames("mt-4 flex items-center", canDelete ? "justify-between" : "justify-end"),
                children: [
                    canDelete && /*#__PURE__*/ _jsx(Button, {
                        appearance: "ghost",
                        color: "red",
                        className: modalType === "edit" ? "visible" : "invisible",
                        "data-testid": CUSTOM_FIELD_DELETE,
                        onClick: ()=>showDeleteModal({
                                handleConfirmDeleteClick: handleDelete
                            }),
                        size: "large",
                        children: "Delete"
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(Button, {
                                appearance: "ghost",
                                color: "grey",
                                "data-testid": CUSTOM_FIELD_MODAL_CANCEL,
                                onClick: onClose,
                                size: "large",
                                children: "Cancel"
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                appearance: "filled",
                                color: "blue",
                                "data-testid": CUSTOM_FIELD_MODAL_SAVE,
                                disabled: isSaveDisabled,
                                form: CUSTOM_FIELD_MODAL_FORM_ID,
                                isLoading: isLoading,
                                ref: saveButtonRef,
                                size: "large",
                                type: "submit",
                                children: "Save"
                            })
                        ]
                    })
                ]
            })
        ]
    });
};
